import { PRIVATE_API_ROUTES, PUBLIC_API_ROUTES } from "../constants";
import { setAuth, setUser } from "../globalContext/auth/authActions";
import Http from "./http-service";

import {
  decryptAuthData,
  encrypt,
  getLocalizedMessage,
} from "../UtilityFunctions/utils";
import { showNotification } from "../UtilityFunctions/notification";
import {
  ACCOUNT_ALREADY_LOGIN,
  EXCEED_OTP_LIMIT,
  INVALID_OTP,
  INVALID_RESET_PASSWORD_CODE,
  INVALID_USER_LOGIN,
  USER_BLOCKED,
  USER_NOT_FOUND,
} from "../constants/errorConstants";
import { setLoading } from "../globalContext/globalLoader/globalLoaderAction";
import StorageService from "./storage-service";
import { setConversation } from "../globalContext/coversation/coversationActions";

class AuthService {
  static async login(body, dispatch, intl, navigate) {
    const encryptedUserData = {
      email: body?.email,
      password: encrypt(body?.password),
    };

    return Http.post(PUBLIC_API_ROUTES.LOGIN, encryptedUserData)
      .then((res) => {
        const authorization = res.headers.get("authorization");
        dispatch(
          setAuth({
            email: body.email,
            password: body.password,
            token: authorization,
          })
        );
        return Promise.resolve(res)
      })
      .catch((err) => {
        if (err?.response?.data?.code === EXCEED_OTP_LIMIT) {
          showNotification(
            "error",
            "Error",
            getLocalizedMessage(intl, "login.otpLimit")
          );
        } else if (err?.response?.data?.code === INVALID_USER_LOGIN) {
          showNotification(
            "error",
            "Error",
            getLocalizedMessage(intl, "login.invalidCredentialsError")
          );
        } else if (err?.response?.data?.code === ACCOUNT_ALREADY_LOGIN) {
          showNotification(
            "error",
            "Error",
            getLocalizedMessage(intl, "login.maxLoginUserLimitReached")
          );
        } else if (err?.response?.data?.code === USER_BLOCKED) {
          showNotification(
            "error",
            "Error",
            getLocalizedMessage(intl, "login.userBlocked")
          );
        } else {
          showNotification(
            "error",
            "Error",
            getLocalizedMessage(intl, "error.somethingWentWrong")
          );
        }
        return Promise.reject(err)
      }); // Http.post(`/users/sign-in`, body)
  }

  static async forgotPassword(body, intl) {
    return Http.post(PUBLIC_API_ROUTES.FORGOT_PASSWORD, body)
      .then(({ data }) => {
        showNotification(
          "success",
          "Success",
          getLocalizedMessage(intl, "login.resetMailSentMessage")
        );
      })
      .catch((error) => {
        if (error?.response?.data?.code === USER_NOT_FOUND) {
          showNotification(
            "success",
            "Success",
            getLocalizedMessage(intl, "login.resetMailSentMessage")
          );
        } else {
          showNotification(
            "error",
            "Error",
            getLocalizedMessage(intl, "error.somethingWentWrong")
          );
        }
      });
  }

  static async submitOtp(dispatch, body, config, intl, setError) {
    return Http.post(PUBLIC_API_ROUTES.VERIFY_OTP, body, config)
      .then((res) => {
        StorageService.set("token", {
          token: res?.headers?.authorization,
        });
        const userData = decryptAuthData(res?.headers?.authorization);
        dispatch(setUser(userData));
        showNotification(
          "success",
          "Success",
          getLocalizedMessage(intl, "login.success")
        );
        return Promise.resolve(res)
      })
      .catch((err) => {
        if (err?.response?.data?.code === INVALID_OTP) {
          setError(getLocalizedMessage(intl, "login.wrongOtpError"));
        } else {
          showNotification(
            "error",
            "Error",
            getLocalizedMessage(intl, "error.somethingWentWrong")
          );
        }
        return Promise.reject(err)
      });
  }

  static async getConversationTokenAndSid({ dispatch, intl }) {
    return Http.get(PUBLIC_API_ROUTES.CONVERSATION_TOKEN)
      .then((res) => {
        dispatch(
          setConversation({
            ...res.data.data,
            conversationToken: res?.headers["conversation"],
          })
        );
        return Promise.resolve(res.data.data);
      })
      .catch((err) => {
        showNotification(
          "error",
          "Error",
          getLocalizedMessage(intl, "error.somethingWentWrong")
        );
        return Promise.reject(err);
      });
  }

  static async resendOtp(body, config, intl, setResendOtpLimitExceed) {
    return Http.post(PUBLIC_API_ROUTES.RESEND_OTP, body, config)
      .then((res) => {
        showNotification(
          "success",
          "Success",
          getLocalizedMessage(intl, "login.resendOtpSuccess")
        );
      })
      .catch((err) => {
        if (err?.response?.data?.code === EXCEED_OTP_LIMIT) {
          setResendOtpLimitExceed(true);
        } else {
          showNotification(
            "error",
            "Error",
            getLocalizedMessage(intl, "error.somethingWentWrong")
          );
        }
      });
  }

  static async resetPassword({ body, intl }) {
    return Http.put(PUBLIC_API_ROUTES.RESET_PASSWORD, body)
      .then(({ data }) => {
        return Promise.resolve(data);
      })
      .catch((error) => {
        if (error?.response?.data?.code === INVALID_RESET_PASSWORD_CODE) {
          showNotification(
            "error",
            "Error",
            getLocalizedMessage(intl, "login.resetLinkExpired")
          );
        } else {
          showNotification(
            "error",
            "Error",
            getLocalizedMessage(intl, "error.somethingWentWrong")
          );
        }
        return Promise.reject(error);
      });
  }

  static async logOut({ intl }, showPopUpMsg = true) {
    return Http.put(PUBLIC_API_ROUTES.LOG_OUT, {})
      .then(({ data }) => {
        if (showPopUpMsg) {
          showNotification(
            "success",
            "Success",
            getLocalizedMessage(intl, "login.loggedOut")
          );
        }
        return Promise.resolve(data);
      })
      .catch((error) => {
        return Promise.reject(error);
      });
  }

  static async switchProfile({ dispatch, loaderDispatch, role, intl }) {
    loaderDispatch(setLoading(true));
    return Http.get(`${PRIVATE_API_ROUTES.SWITCH_PROFILE(role.id)}`)
      .then((res) => {
        StorageService.set("token", {
          token: res?.headers?.authorization,
        });
        const userData = decryptAuthData(res?.headers?.authorization);
        dispatch(setUser(userData));
        loaderDispatch(setLoading(false));
        showNotification(
          "success",
          "Success",
          getLocalizedMessage(intl, "switchProfile.successMsg", {
            role: role.fullName,
          })
        );
        return Promise.resolve(res);
      })
      .catch((err) => {
        loaderDispatch(setLoading(false));
        showNotification(
          "error",
          "Error",
          getLocalizedMessage(intl, "error.somethingWentWrong")
        );
        return Promise.reject(err);
      });
  }
}

export default AuthService;
